import { shipmentService } from '../services/shipmentService.js'
import { parseBlobErrorMessage, printPdfBlob } from './printPdfBlob.js'
import { downloadBlob } from './downloadBlob.js'
import { printHtmlInFrame } from './printShipmentReceipt.js'
import Alert from '../components/common/Alert.js'

const QR_PRINT_ROLES = ['System Admin', 'Admin', 'Accountant', 'Hub Admin']

export function canPrintShipmentQr(user) {
  return QR_PRINT_ROLES.includes(user?.role)
}

async function bodyText(data) {
  if (typeof data === 'string') return data
  if (data instanceof Blob) return data.text()
  return ''
}

function messageFromBody(text) {
  const raw = String(text || '').trim()
  if (!raw || raw.startsWith('%PDF') || raw.includes('<html')) {
    return 'Failed to print QR code'
  }
  try {
    const parsed = JSON.parse(raw)
    return parsed.error || parsed.message || 'Failed to print QR code'
  } catch {
    return 'Failed to print QR code'
  }
}

async function qrPrintErrorMessage(error) {
  const data = error?.response?.data
  if (typeof data === 'string' || data instanceof Blob) {
    return messageFromBody(await bodyText(data))
  }
  return parseBlobErrorMessage(error, 'Failed to print QR code')
}

function responseType(response) {
  const header = response?.headers?.['content-type'] || response?.headers?.['Content-Type'] || ''
  const blobType = response?.data?.type || ''
  return `${header} ${blobType}`.toLowerCase()
}

async function finishPdfPrint(blob, shipmentId, silent) {
  const result = await printPdfBlob(blob, `QR_Code_${shipmentId}.pdf`)
  if (result?.method === 'cancelled') return
  if (!silent && result?.method === 'blocked') {
    Alert.error('Could not open the print dialog')
  }
}

export async function printShipmentQrCode(shipmentId, { onClose, silent = false, template } = {}) {
  try {
    if (onClose) onClose()
    const response = await shipmentService.getQRCodeHtml(shipmentId, template)
    const payload = response?.data
    const type = responseType(response)
    if (type.includes('pdf')) {
      const blob = payload instanceof Blob
        ? payload
        : new Blob([payload], { type: 'application/pdf' })
      await finishPdfPrint(blob, shipmentId, silent)
      return
    }
    const html = await bodyText(payload)
    if (html.startsWith('%PDF')) {
      const blob = payload instanceof Blob
        ? payload
        : new Blob([payload], { type: 'application/pdf' })
      await finishPdfPrint(blob, shipmentId, silent)
      return
    }
    if (!html.includes('<html')) {
      throw new Error(messageFromBody(html))
    }
    const printed = await printHtmlInFrame(html, `QR Code ${shipmentId}`)
    if (printed === 'cancelled') return
    if (!printed && !silent) {
      Alert.error('Could not open the print dialog')
    }
  } catch (error) {
    if (!silent) {
      Alert.error(await qrPrintErrorMessage(error))
    }
  }
}

/** Extra QR print after booking when the location hub has Print QR on.
 * Uses that hub's template: A4 2×2 sheet or 3 inch card.
 */
export async function maybePrintQrWithBookingReceipt(user, shipmentId) {
  if (!user?.print_qr_with_booking_receipt || !shipmentId) return
  const template = user?.qr_print_template === 'card_3inch' ? 'card_3inch' : 'small'
  await printShipmentQrCode(shipmentId, { template })
}

export async function downloadShipmentQrCodesBulk(shipmentIds, filename = 'shipment_qr_codes.pdf') {
  const ids = (shipmentIds || []).filter(Boolean)
  if (!ids.length) {
    Alert.error('No shipments found for QR download')
    return
  }
  try {
    const response = await shipmentService.bulkDownloadQRCodes(ids)
    await downloadBlob(response.data, filename, 'application/pdf')
    Alert.success('Shipment QR codes downloaded')
  } catch (error) {
    Alert.error(await parseBlobErrorMessage(error, 'Failed to download shipment QR codes'))
  }
}
